/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Box } from '@mui/material';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import EcosystemHeader from 'components/Header/EcosystemHeader';
import ExpiryWarning from 'components/ExpiryWarning';
import Footer from 'components/Footer';
import LoadingSpinner from 'components/LoadingSpinner';
import EcosystemsPage from 'views/Ecosystems';
import EcosystemDashboard from 'views/EcosystemDashboard';
import ThreatReport from 'views/ThreatReport';
import { fetchEcosystems } from 'views/Ecosystems/scenario-actions';
import { ECOSYSTEM_STATUS, ROUTES } from 'enum';

import './ecosystems.scss';
import { loadEcosystemStarted } from 'store/ecosystems/actions';
import { setInitialObjectLoaded } from 'store/objects/actions';

const Ecosystems = ({ ecosystems, fetchEcosystems, resetInitialEcosystemLoaded, resetInitialObjectLoaded }) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const { cusID } = useParams();

  const isEcosystemAvailable = useMemo(
    () =>
      ecosystems.some((ecosystem) =>
        [ECOSYSTEM_STATUS.PENDING_CHANGES, ECOSYSTEM_STATUS.OK, ECOSYSTEM_STATUS.COMMIT_IN_PROGRESS].includes(
          ecosystem.status,
        ),
      ),
    [ecosystems],
  );

  const [activeTab, setActiveTab] = useState(pathname.split('/').pop() === 'dashboard' ? 'summary' : 'all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    resetInitialEcosystemLoaded();
    resetInitialObjectLoaded();
  }, []);

  const getEcosystems = useCallback(async () => {
    try {
      setLoading(true);
      await fetchEcosystems(cusID);
    } finally {
      setLoading(false);
    }
  }, [fetchEcosystems, cusID]);

  useEffect(() => {
    const tabName = pathname.split('/').at(-1);
    if (tabName === 'summary') setActiveTab('summary');
    else if (tabName === 'threat-report') setActiveTab('threat');
    getEcosystems();
  }, [getEcosystems, cusID]);

  const handleChangeTab = (tab) => {
    if (!isEcosystemAvailable) {
      setActiveTab('all');
      return;
    }
    setActiveTab(tab);
    if (tab === 'summary') history.replace(`${ROUTES.CUSTOMERS}/${cusID}/ecosystems/dashboard`);
    else if (tab === 'threat') history.replace(`${ROUTES.CUSTOMERS}/${cusID}/ecosystems/threat-report`);
    else history.replace(`${ROUTES.CUSTOMERS}/${cusID}/ecosystems`);
  };

  const renderContents = () => {
    if (loading) {
      return <LoadingSpinner />;
    }
    if (activeTab === 'all') return <EcosystemsPage />;
    if (activeTab === 'threat') {
      return (
        <Box sx={{ px: 7, pt: 3, pb: 7 }}>
          <ThreatReport />
        </Box>
      );
    }
    return (
      <Box sx={{ px: 7, pt: 3, pb: 7 }}>
        <EcosystemDashboard />
      </Box>
    );
  };

  return (
    <div className="wrapper ecosystems-wrapper">
      <ExpiryWarning />
      <EcosystemHeader
        onChangeTab={handleChangeTab}
        activeTab={activeTab}
        isEcosystemAvailable={isEcosystemAvailable}
      />
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
        <Box sx={{ width: '100%', bgcolor: '#f9f9f9', flex: 1 }}>{renderContents()}</Box>
        <Footer />
      </Box>
    </div>
  );
};

Ecosystems.propTypes = {
  fetchEcosystems: PropTypes.func.isRequired,
  ecosystems: PropTypes.array.isRequired,
  resetInitialEcosystemLoaded: PropTypes.func,
  resetInitialObjectLoaded: PropTypes.func,
};

const mapStateToProps = (state) => ({
  ecosystems: state.ecosystems.items,
});

const mapDispatchToProps = (dispatch) => ({
  fetchEcosystems: (cusID) => dispatch(fetchEcosystems(cusID)),
  resetInitialEcosystemLoaded: () => dispatch(loadEcosystemStarted()),
  resetInitialObjectLoaded: () => dispatch(setInitialObjectLoaded(false)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Ecosystems);
